import { criarCatalogoFiltros } from '@/utils/catalogoFiltros';
import {
    AGRUPAMENTOS_TAXA,
    CAMPOS_DATA_COBRANCA,
    CAMPOS_DATA_TAXA,
    CONTAS_MP,
    FORMAS_PAGAMENTO,
    GATEWAYS,
    METODOS_MP,
    NOTA_FISCAL,
    PERIODICIDADES,
    SIM_NAO,
    SITUACAO_EMPRESA,
    STATUS_ASSINATURA,
    STATUS_COBRANCA,
} from './status';

/**
 * Catálogos de filtros das duas abas da tela de cobranças.
 *
 * Cada entrada aqui tem o `where` correspondente em `CobrancaConsultaFilter` /
 * `TaxaMercadoPagoFilter`. Campo sem par no backend é filtro que não filtra.
 */

export const CAMPOS_COBRANCAS = [
    {
        chave: 'campo_data',
        titulo: 'Filtrar período por',
        grupo: 'Período',
        tipo: 'select',
        cols: 12,
        padrao: 'vencimento',
        sempreEnviar: true,
        dica: 'Vencimento responde "quem está devendo". Pagamento exclui, por definição, quem não pagou.',
        opcoes: CAMPOS_DATA_COBRANCA,
    },
    {
        chave: 'status',
        titulo: 'Status',
        grupo: 'Cobrança',
        tipo: 'multi',
        cols: 6,
        opcoes: STATUS_COBRANCA,
    },
    {
        chave: 'forma_pagamento',
        titulo: 'Forma de pagamento',
        grupo: 'Cobrança',
        tipo: 'multi',
        cols: 6,
        opcoes: FORMAS_PAGAMENTO,
    },
    {
        chave: 'gateway',
        titulo: 'Gateway',
        grupo: 'Cobrança',
        tipo: 'multi',
        cols: 6,
        opcoes: GATEWAYS,
    },
    {
        chave: 'nota_fiscal',
        titulo: 'Nota fiscal',
        grupo: 'Cobrança',
        tipo: 'multi',
        cols: 6,
        opcoes: NOTA_FISCAL,
    },
    {
        chave: 'valor_min',
        titulo: 'Valor de',
        grupo: 'Cobrança',
        tipo: 'numero',
        cols: 3,
        prefixo: 'R$',
        min: 0,
    },
    {
        chave: 'valor_max',
        titulo: 'Valor até',
        grupo: 'Cobrança',
        tipo: 'numero',
        cols: 3,
        prefixo: 'R$',
        min: 0,
    },
    {
        chave: 'atraso_min',
        titulo: 'Vencidas há mais de',
        grupo: 'Cobrança',
        tipo: 'numero',
        cols: 3,
        sufixo: 'dias',
        dica: 'Só o que ainda pode ser cobrado.',
    },
    {
        chave: 'pagamento_parcial',
        titulo: 'Pagamento parcial',
        grupo: 'Cobrança',
        tipo: 'select',
        cols: 3,
        dica: 'Pago por menos que o total.',
        opcoes: SIM_NAO,
    },
    {
        chave: 'com_alteracao_vencimento',
        titulo: 'Teve alteração de vencimento',
        grupo: 'Cobrança',
        tipo: 'select',
        cols: 6,
        opcoes: SIM_NAO,
    },
    {
        chave: 'primeira_cobranca',
        titulo: '1ª cobrança paga da empresa',
        grupo: 'Cobrança',
        tipo: 'select',
        cols: 6,
        dica: 'O "novo MRR": a linha em que o cliente virou receita.',
        opcoes: [{ value: 'true', text: 'Somente a primeira' }],
    },
    {
        chave: 'situacao_empresa',
        titulo: 'Situação da empresa',
        grupo: 'Empresa / Assinatura',
        tipo: 'multi',
        cols: 4,
        opcoes: SITUACAO_EMPRESA,
    },
    {
        chave: 'status_assinatura',
        titulo: 'Status da assinatura',
        grupo: 'Empresa / Assinatura',
        tipo: 'multi',
        cols: 4,
        opcoes: STATUS_ASSINATURA,
    },
    {
        chave: 'periodicidade',
        titulo: 'Periodicidade',
        grupo: 'Empresa / Assinatura',
        tipo: 'multi',
        cols: 4,
        opcoes: PERIODICIDADES,
    },
];

export const CAMPOS_TAXAS = [
    {
        chave: 'campo_data',
        titulo: 'Filtrar período por',
        grupo: 'Período',
        tipo: 'select',
        cols: 6,
        padrao: 'paid_at',
        sempreEnviar: true,
        dica: '"Pagamento" é o instante em que o webhook do Mercado Pago chegou.',
        opcoes: CAMPOS_DATA_TAXA,
    },
    {
        chave: 'agrupar_por',
        titulo: 'Agrupar por',
        grupo: 'Período',
        tipo: 'select',
        cols: 6,
        padrao: '',
        dica: 'Agrupar troca as colunas do grid. Os totalizadores não mudam.',
        opcoes: AGRUPAMENTOS_TAXA,
    },
    {
        chave: 'metodo',
        titulo: 'Método',
        grupo: 'Transação',
        tipo: 'multi',
        cols: 6,
        opcoes: METODOS_MP,
    },
    {
        chave: 'conta',
        titulo: 'Conta que recebeu',
        grupo: 'Transação',
        tipo: 'multi',
        cols: 6,
        dica: 'Taxa registrada antes da separação de contas conta como PF.',
        opcoes: CONTAS_MP,
    },
    {
        chave: 'empresa_id',
        titulo: 'Empresa',
        grupo: 'Transação',
        tipo: 'multi',
        cols: 6,
        // Preenchido em runtime com as empresas que já geraram taxa (GET taxas-mercado-pago/empresas).
        opcoes: [],
    },
    {
        chave: 'valor_min',
        titulo: 'Transação de',
        grupo: 'Transação',
        tipo: 'numero',
        cols: 3,
        prefixo: 'R$',
        min: 0,
    },
    {
        chave: 'valor_max',
        titulo: 'Transação até',
        grupo: 'Transação',
        tipo: 'numero',
        cols: 3,
        prefixo: 'R$',
        min: 0,
    },
    {
        chave: 'taxa_min',
        titulo: 'Taxa de',
        grupo: 'Transação',
        tipo: 'numero',
        cols: 3,
        prefixo: 'R$',
        min: 0,
    },
    {
        chave: 'taxa_max',
        titulo: 'Taxa até',
        grupo: 'Transação',
        tipo: 'numero',
        cols: 3,
        prefixo: 'R$',
        min: 0,
    },
    {
        chave: 'percentual_min',
        titulo: '% efetivo de',
        grupo: 'Transação',
        tipo: 'numero',
        cols: 3,
        sufixo: '%',
        min: 0,
        dica: 'Acha linha cobrada fora da taxa configurada.',
    },
    {
        chave: 'percentual_max',
        titulo: '% efetivo até',
        grupo: 'Transação',
        tipo: 'numero',
        cols: 3,
        sufixo: '%',
        min: 0,
    },
    {
        chave: 'situacao_empresa',
        titulo: 'Situação da empresa',
        grupo: 'Empresa',
        tipo: 'multi',
        cols: 6,
        opcoes: SITUACAO_EMPRESA,
    },
    {
        chave: 'mp_conectado',
        titulo: 'Mercado Pago conectado',
        grupo: 'Empresa',
        tipo: 'select',
        cols: 6,
        dica: 'Conta conectada por OAuth hoje — a taxa antiga continua na lista mesmo se desconectou.',
        opcoes: SIM_NAO,
    },
];

export const catalogoFiltrosCobrancas = criarCatalogoFiltros(CAMPOS_COBRANCAS);
export const catalogoFiltrosTaxas = criarCatalogoFiltros(CAMPOS_TAXAS);

/**
 * Injeta as empresas carregadas da API no campo de filtro. Mutação deliberada do catálogo: ele é o
 * módulo único que o painel, os chips e o getParams leem — manter a lista fora dele faria o chip
 * mostrar o id cru em vez do nome da empresa.
 */
export function definirEmpresasDoFiltroTaxas(empresas) {
    const campo = CAMPOS_TAXAS.find(c => c.chave === 'empresa_id');

    campo.opcoes = empresas.map(e => ({ value: String(e.id), text: `${e.id} - ${e.fantasia}` }));
}
